import React, { useMemo, useState } from 'react';
import { useConfig } from '../../context/ConfigContext';
import { useToast } from '../Toast';
import { C, S } from '../../utils/styles';

const US_STATE_OPTIONS = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM',
  'NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA',
  'WV','WI','WY',
];

function money(n) { return `$${Number(n || 0).toFixed(2)}`; }

export default function MinimumWageJurisdictionsEditor() {
  const { config, updateConfig } = useConfig();
  const showToast = useToast();
  const j = config?.minimumWageJurisdictions || { federal: { hourly: 7.25 }, states: {}, cities: {} };

  const [federalDraft, setFederalDraft] = useState(String(j.federal?.hourly ?? 7.25));
  const [federalEffective, setFederalEffective] = useState(j.federal?.effectiveDate || '');
  const [stateAddCode, setStateAddCode] = useState('');
  const [stateAddRate, setStateAddRate] = useState('');
  const [stateAddDate, setStateAddDate] = useState('');
  const [cityAddName, setCityAddName] = useState('');
  const [cityAddState, setCityAddState] = useState('');
  const [cityAddRate, setCityAddRate] = useState('');
  const [cityAddDate, setCityAddDate] = useState('');

  const stateRows = useMemo(() => {
    return Object.entries(j.states || {}).sort(([a], [b]) => a.localeCompare(b));
  }, [j.states]);
  const cityRows = useMemo(() => {
    return Object.entries(j.cities || {}).sort(([a], [b]) => a.localeCompare(b));
  }, [j.cities]);

  const persist = (next) => updateConfig({ minimumWageJurisdictions: next });

  const saveFederal = async () => {
    const v = parseFloat(federalDraft);
    if (Number.isNaN(v) || v <= 0) {
      showToast('Federal minimum wage must be a positive number', 'error');
      return;
    }
    await persist({
      ...j,
      federal: { ...(j.federal || {}), hourly: v, effectiveDate: federalEffective || j.federal?.effectiveDate || null, lastUpdated: new Date().toISOString() },
    });
    showToast('Federal minimum wage saved', 'success');
  };

  const upsertState = async (code, hourly, effectiveDate) => {
    if (!code) return;
    const v = parseFloat(hourly);
    if (Number.isNaN(v) || v <= 0) { showToast('Hourly rate must be > 0', 'error'); return; }
    const next = { ...j, states: { ...(j.states || {}), [code]: { hourly: v, effectiveDate: effectiveDate || null } } };
    await persist(next);
    showToast(`${code} state minimum wage saved`, 'success');
  };
  const removeState = async (code) => {
    const states = { ...(j.states || {}) };
    delete states[code];
    await persist({ ...j, states });
    showToast(`${code} removed`, 'success');
  };

  const upsertCity = async (key, hourly, state, effectiveDate) => {
    if (!key) return;
    const v = parseFloat(hourly);
    if (Number.isNaN(v) || v <= 0) { showToast('Hourly rate must be > 0', 'error'); return; }
    const next = { ...j, cities: { ...(j.cities || {}), [key]: { hourly: v, state: (state || '').toUpperCase() || null, effectiveDate: effectiveDate || null } } };
    await persist(next);
    showToast(`${key} saved`, 'success');
  };
  const removeCity = async (key) => {
    const cities = { ...(j.cities || {}) };
    delete cities[key];
    await persist({ ...j, cities });
    showToast(`${key} removed`, 'success');
  };

  const handleAddState = async () => {
    if (!stateAddCode || !stateAddRate) { showToast('State and rate are required', 'error'); return; }
    await upsertState(stateAddCode.toUpperCase(), stateAddRate, stateAddDate);
    setStateAddCode(''); setStateAddRate(''); setStateAddDate('');
  };
  const handleAddCity = async () => {
    if (!cityAddName || !cityAddState || !cityAddRate) { showToast('Jurisdiction, state, and rate are required', 'error'); return; }
    const key = `${cityAddName.trim()}, ${cityAddState.trim().toUpperCase()}`;
    await upsertCity(key, cityAddRate, cityAddState, cityAddDate);
    setCityAddName(''); setCityAddState(''); setCityAddRate(''); setCityAddDate('');
  };

  return (
    <div data-testid="minimum-wage-jurisdictions-editor" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ ...S.card }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={S.heading}>Minimum Wage by Jurisdiction</div>
            <p style={{ color: C.textMid, margin: 0, fontSize: 14, maxWidth: 640 }}>
              The viability check uses the highest applicable minimum wage for each employee — federal, state, or city/county. Employees living in NYC, Seattle, San Francisco, or other listed cities pick up the city floor automatically.
            </p>
          </div>
          <button
            type="button"
            data-testid="min-wage-sync-btn"
            disabled
            title="Sync via PayrollTaxAPI will be wired in a follow-up phase"
            style={{ ...S.buttonSecondary, opacity: 0.5, cursor: 'not-allowed' }}
          >
            ↻ Sync from PayrollTaxAPI (pending)
          </button>
        </div>
      </div>

      {/* Federal */}
      <div style={S.card}>
        <div style={S.heading}>Federal Floor</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: '0 0 200px' }}>
            <label style={S.label}>Hourly</label>
            <input
              data-testid="min-wage-federal-rate"
              type="number" step="0.01"
              value={federalDraft}
              onChange={(e) => setFederalDraft(e.target.value)}
              style={S.input}
            />
          </div>
          <div style={{ flex: '0 0 200px' }}>
            <label style={S.label}>Effective Date</label>
            <input
              data-testid="min-wage-federal-effective"
              type="date"
              value={federalEffective ? federalEffective.slice(0, 10) : ''}
              onChange={(e) => setFederalEffective(e.target.value)}
              style={S.input}
            />
          </div>
          <button data-testid="min-wage-federal-save" type="button" onClick={saveFederal} style={S.buttonPrimary}>Save Federal</button>
        </div>
      </div>

      {/* States */}
      <div style={S.card}>
        <div style={S.heading}>States (override federal where higher)</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
            <thead>
              <tr>
                <th style={S.th}>State</th>
                <th style={S.th}>Hourly</th>
                <th style={S.th}>Effective Date</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {stateRows.length === 0 && (
                <tr><td style={{ ...S.td, color: C.textLight, fontStyle: 'italic' }} colSpan={4}>No state-level overrides configured. Add one below.</td></tr>
              )}
              {stateRows.map(([code, entry]) => (
                <StateRow key={code} code={code} entry={entry} onSave={upsertState} onRemove={removeState} />
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 16, flexWrap: 'wrap', borderTop: `1px dashed ${C.border}`, paddingTop: 16 }}>
          <div style={{ flex: '0 0 100px' }}>
            <label style={S.label}>State Code</label>
            <select data-testid="min-wage-state-add-code" value={stateAddCode} onChange={(e) => setStateAddCode(e.target.value)} style={S.select}>
              <option value="">—</option>
              {US_STATE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ flex: '0 0 140px' }}>
            <label style={S.label}>Hourly</label>
            <input data-testid="min-wage-state-add-rate" type="number" step="0.01" value={stateAddRate} onChange={(e) => setStateAddRate(e.target.value)} style={S.input} />
          </div>
          <div style={{ flex: '0 0 180px' }}>
            <label style={S.label}>Effective Date</label>
            <input data-testid="min-wage-state-add-date" type="date" value={stateAddDate} onChange={(e) => setStateAddDate(e.target.value)} style={S.input} />
          </div>
          <button data-testid="min-wage-state-add-btn" type="button" onClick={handleAddState} style={S.buttonPrimary}>+ Add State</button>
        </div>
      </div>

      {/* Cities */}
      <div style={S.card}>
        <div style={S.heading}>Cities & Counties (override state where higher)</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
            <thead>
              <tr>
                <th style={S.th}>Jurisdiction</th>
                <th style={S.th}>Hourly</th>
                <th style={S.th}>State</th>
                <th style={S.th}>Effective</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {cityRows.length === 0 && (
                <tr><td style={{ ...S.td, color: C.textLight, fontStyle: 'italic' }} colSpan={5}>No city/county overrides configured. Add one below.</td></tr>
              )}
              {cityRows.map(([key, entry]) => (
                <CityRow key={key} keyName={key} entry={entry} onSave={(k, r, st, d) => upsertCity(k, r, st, d)} onRemove={removeCity} />
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 16, flexWrap: 'wrap', borderTop: `1px dashed ${C.border}`, paddingTop: 16 }}>
          <div style={{ flex: '1 1 200px', minWidth: 200 }}>
            <label style={S.label}>Jurisdiction (city/county name only)</label>
            <input data-testid="min-wage-city-add-name" placeholder="New York City" value={cityAddName} onChange={(e) => setCityAddName(e.target.value)} style={S.input} />
          </div>
          <div style={{ flex: '0 0 100px' }}>
            <label style={S.label}>State</label>
            <select data-testid="min-wage-city-add-state" value={cityAddState} onChange={(e) => setCityAddState(e.target.value)} style={S.select}>
              <option value="">—</option>
              {US_STATE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div style={{ flex: '0 0 130px' }}>
            <label style={S.label}>Hourly</label>
            <input data-testid="min-wage-city-add-rate" type="number" step="0.01" value={cityAddRate} onChange={(e) => setCityAddRate(e.target.value)} style={S.input} />
          </div>
          <div style={{ flex: '0 0 170px' }}>
            <label style={S.label}>Effective Date</label>
            <input data-testid="min-wage-city-add-date" type="date" value={cityAddDate} onChange={(e) => setCityAddDate(e.target.value)} style={S.input} />
          </div>
          <button data-testid="min-wage-city-add-btn" type="button" onClick={handleAddCity} style={S.buttonPrimary}>+ Add Jurisdiction</button>
        </div>
      </div>

      <div style={{ ...S.card, background: C.accentBg, borderColor: C.accentLight }}>
        <div style={{ ...S.heading, color: C.accent }}>How it's applied</div>
        <p style={{ margin: 0, color: C.textMid, fontSize: 13, lineHeight: 1.6 }}>
          For each employee, the engine resolves their location (explicit city &gt; ZIP lookup &gt; employer HQ) and uses <strong>max(federal, state, city)</strong> as the minimum wage floor for the viability check. The chosen jurisdiction and rate appear in each employee's audit trail under <strong>Step 8 — Minimum wage compliance</strong>.
        </p>
      </div>
    </div>
  );
}

function StateRow({ code, entry, onSave, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [hourly, setHourly] = useState(String(entry?.hourly ?? ''));
  const [eff, setEff] = useState(entry?.effectiveDate || '');
  if (!editing) {
    return (
      <tr>
        <td style={S.td}><strong>{code}</strong></td>
        <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace" }}>{money(entry.hourly)}</td>
        <td style={S.td}>{entry.effectiveDate || '—'}</td>
        <td style={{ ...S.td, textAlign: 'right' }}>
          <button data-testid={`min-wage-state-edit-${code}`} type="button" onClick={() => setEditing(true)} style={{ ...S.buttonSecondary, padding: '6px 12px', fontSize: 12 }}>Edit</button>
          <button data-testid={`min-wage-state-remove-${code}`} type="button" onClick={() => onRemove(code)} style={{ ...S.buttonDanger, padding: '6px 12px', fontSize: 12, marginLeft: 6 }}>Remove</button>
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td style={S.td}><strong>{code}</strong></td>
      <td style={S.td}><input type="number" step="0.01" value={hourly} onChange={(e) => setHourly(e.target.value)} style={{ ...S.input, padding: '4px 8px' }} /></td>
      <td style={S.td}><input type="date" value={eff ? eff.slice(0, 10) : ''} onChange={(e) => setEff(e.target.value)} style={{ ...S.input, padding: '4px 8px' }} /></td>
      <td style={{ ...S.td, textAlign: 'right' }}>
        <button type="button" onClick={async () => { await onSave(code, hourly, eff); setEditing(false); }} style={{ ...S.buttonPrimary, padding: '6px 12px', fontSize: 12 }}>Save</button>
        <button type="button" onClick={() => setEditing(false)} style={{ ...S.buttonSecondary, padding: '6px 12px', fontSize: 12, marginLeft: 6 }}>Cancel</button>
      </td>
    </tr>
  );
}

function CityRow({ keyName, entry, onSave, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [hourly, setHourly] = useState(String(entry?.hourly ?? ''));
  const [stateCode, setStateCode] = useState(entry?.state || '');
  const [eff, setEff] = useState(entry?.effectiveDate || '');
  if (!editing) {
    return (
      <tr>
        <td style={S.td}><strong>{keyName}</strong></td>
        <td style={{ ...S.td, fontFamily: "'JetBrains Mono', monospace" }}>{money(entry.hourly)}</td>
        <td style={S.td}>{entry.state || '—'}</td>
        <td style={S.td}>{entry.effectiveDate || '—'}</td>
        <td style={{ ...S.td, textAlign: 'right' }}>
          <button type="button" onClick={() => setEditing(true)} style={{ ...S.buttonSecondary, padding: '6px 12px', fontSize: 12 }}>Edit</button>
          <button type="button" onClick={() => onRemove(keyName)} style={{ ...S.buttonDanger, padding: '6px 12px', fontSize: 12, marginLeft: 6 }}>Remove</button>
        </td>
      </tr>
    );
  }
  return (
    <tr>
      <td style={S.td}><strong>{keyName}</strong></td>
      <td style={S.td}><input type="number" step="0.01" value={hourly} onChange={(e) => setHourly(e.target.value)} style={{ ...S.input, padding: '4px 8px' }} /></td>
      <td style={S.td}>
        <select value={stateCode} onChange={(e) => setStateCode(e.target.value)} style={{ ...S.select, padding: '4px 8px' }}>
          <option value="">—</option>
          {US_STATE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </td>
      <td style={S.td}><input type="date" value={eff ? eff.slice(0, 10) : ''} onChange={(e) => setEff(e.target.value)} style={{ ...S.input, padding: '4px 8px' }} /></td>
      <td style={{ ...S.td, textAlign: 'right' }}>
        <button type="button" onClick={async () => { await onSave(keyName, hourly, stateCode, eff); setEditing(false); }} style={{ ...S.buttonPrimary, padding: '6px 12px', fontSize: 12 }}>Save</button>
        <button type="button" onClick={() => setEditing(false)} style={{ ...S.buttonSecondary, padding: '6px 12px', fontSize: 12, marginLeft: 6 }}>Cancel</button>
      </td>
    </tr>
  );
}
